/**
 * CategorySettingsPanel — form for one Platform Settings category (non-collapsible).
 * Parent may keep multiple instances mounted with isVisible=false to preserve unsaved edits.
 */
import { useState, useCallback, useMemo } from "react";
import { Save01, RefreshCw01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Badge } from "@/components/base/badges/badges";
import { SettingRow } from "./setting-row";
import { SchedulerJobsPanel } from "./scheduler-jobs-panel";
import { DocumentCheckMatrix } from "./document-check-matrix";
import { IDValidationsSection } from "./id-validations-section";
import { MailCompanionPanel } from "./mail-companion-panel";
import { CATEGORY_CONFIG } from "../category-config";
import type { Setting, SettingCategory } from "@/types/api/settings";
import { cx } from "@/utils/cx";

const MATRIX_MANAGED_KEYS = new Set([
    "single_check_doc_types",
    "cross_check_doc_types",
    "issue_creation_doc_types",
]);

export interface CategorySettingsPanelProps {
    category: SettingCategory;
    settings: Setting[];
    onSave: (values: Record<string, unknown>) => Promise<void>;
    onReset: () => void;
    isSaving: boolean;
    /** When false, panel is hidden but stays mounted (preserves local edit state). */
    isVisible: boolean;
}

export function CategorySettingsPanel({
    category,
    settings,
    onSave,
    onReset,
    isSaving,
    isVisible,
}: CategorySettingsPanelProps) {
    const [editedValues, setEditedValues] = useState<Record<string, unknown>>({});
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    const config = CATEGORY_CONFIG[category];
    const Icon = config.icon;

    const isDirty = useMemo(() => {
        return Object.keys(editedValues).some((key) => {
            const setting = settings.find((s) => s.key === key);
            if (!setting) return false;
            return editedValues[key] !== setting.value;
        });
    }, [editedValues, settings]);

    const getValue = useCallback(
        (setting: Setting) => {
            if (setting.key in editedValues) {
                return editedValues[setting.key];
            }
            return setting.value;
        },
        [editedValues]
    );

    const handleChange = useCallback((key: string, value: unknown) => {
        setEditedValues((prev) => ({ ...prev, [key]: value }));
        setValidationErrors((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    const validateValues = useCallback(() => {
        const errors: Record<string, string> = {};

        for (const setting of settings) {
            const value = getValue(setting);
            const rules = setting.validation_rules;

            if (!rules) continue;

            if (setting.value_type === "integer" || setting.value_type === "float") {
                const numValue = Number(value);
                if (rules.min !== undefined && numValue < rules.min) {
                    errors[setting.key] = `Minimum value is ${rules.min}`;
                }
                if (rules.max !== undefined && numValue > rules.max) {
                    errors[setting.key] = `Maximum value is ${rules.max}`;
                }
            }

            if (setting.value_type === "string" && rules.pattern && value) {
                const regex = new RegExp(rules.pattern);
                if (!regex.test(String(value))) {
                    errors[setting.key] = `Value doesn't match required format`;
                }
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [settings, getValue]);

    const handleSave = useCallback(async () => {
        if (!validateValues()) return;

        const changedValues: Record<string, unknown> = {};
        for (const key of Object.keys(editedValues)) {
            const setting = settings.find((s) => s.key === key);
            if (setting && editedValues[key] !== setting.value) {
                changedValues[key] = editedValues[key];
            }
        }

        if (Object.keys(changedValues).length === 0) return;

        await onSave(changedValues);
        setEditedValues({});
    }, [editedValues, settings, validateValues, onSave]);

    const handleReset = useCallback(() => {
        setEditedValues({});
        setValidationErrors({});
        onReset();
    }, [onReset]);

    return (
        <div
            className={cx(
                "rounded-xl bg-primary shadow-xs ring-1 ring-secondary overflow-hidden mb-4",
                !isVisible && "hidden"
            )}
            aria-hidden={!isVisible}
        >
            <div className="w-full flex items-center justify-between gap-3 p-4">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                    <Icon className="size-5 shrink-0 text-fg-quaternary" aria-hidden />
                    <span className="font-semibold text-primary truncate">{config.displayName}</span>
                    {isDirty && (
                        <Badge color="warning" size="sm">
                            Unsaved
                        </Badge>
                    )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    <Button
                        color="secondary"
                        size="sm"
                        iconLeading={RefreshCw01}
                        onClick={handleReset}
                        isDisabled={!isDirty}
                    >
                        Reset
                    </Button>
                    <Button
                        color="primary"
                        size="sm"
                        iconLeading={Save01}
                        onClick={handleSave}
                        isDisabled={!isDirty}
                        isLoading={isSaving}
                    >
                        Save
                    </Button>
                </div>
            </div>

            <div className="border-t border-secondary p-4 sm:p-6">
                {/* Mail Companion has its own full panel — skip generic settings UI */}
                {category === "mail_companion" ? (
                    <MailCompanionPanel />
                ) : (
                    <>
                        <p className="text-sm text-tertiary mb-4">{config.description}</p>

                        <div className="space-y-1">
                            {settings.length === 0 ? (
                                <p className="text-tertiary text-sm py-4 text-center">No settings in this category</p>
                            ) : (
                                settings
                                    .filter(
                                        (s) => category !== "extraction" || !MATRIX_MANAGED_KEYS.has(s.key)
                                    )
                                    .map((setting) => (
                                        <SettingRow
                                            key={setting.key}
                                            setting={setting}
                                            value={getValue(setting)}
                                            onChange={(value) => handleChange(setting.key, value)}
                                            validationError={validationErrors[setting.key]}
                                        />
                                    ))
                            )}
                        </div>

                        {category === "scheduler" && <SchedulerJobsPanel />}

                        {category === "extraction" && <DocumentCheckMatrix extractionSettings={settings} />}

                        {category === "extraction" && <IDValidationsSection />}
                    </>
                )}
            </div>
        </div>
    );
}
